/**
 * BlogLeaderboard — public /blog/leaderboard page. Ranks the top
 * writers this week by Social Capital points earned. Links each
 * entry to `/writers/{id}`.
 */
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Trophy, Sparkles, CheckCircle2 } from "lucide-react";

const BASE = `${process.env.REACT_APP_BACKEND_URL || ""}/api/publisher/v1`;

function Avatar({ url, name, size = 40 }) {
  if (url) return <img src={url} alt="" className="rounded-full object-cover" style={{ width: size, height: size }} loading="lazy" />;
  const initials = (name || "?").trim().split(/\s+/).slice(0, 2).map((s) => s[0]).join("").toUpperCase();
  return (
    <div className="rounded-full bg-teal-100 text-teal-900 font-bold flex items-center justify-center"
         style={{ width: size, height: size, fontSize: Math.round(size * 0.36), fontFamily: "'Lora', serif" }}>
      {initials || "SP"}
    </div>
  );
}

export default function BlogLeaderboard() {
  const [period, setPeriod] = useState("week");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${BASE}/leaderboard?period=${period}&limit=20`);
      setData(r.ok ? await r.json() : { entries: [] });
    } finally {
      setLoading(false);
    }
  }, [period]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="min-h-screen" style={{ background: "#f8f5ef" }} data-testid="blog-leaderboard">
      <div className="max-w-3xl mx-auto px-4 py-10">
        <header className="mb-8">
          <div className="text-xs uppercase tracking-widest text-teal-700 font-bold">Community</div>
          <h1 className="text-4xl font-bold mt-1 text-slate-900" style={{ fontFamily: "'Lora', serif" }}>
            Leaderboard
          </h1>
          <p className="text-sm text-slate-600 mt-1">
            The writers moving conversations forward on SocialPulse Blog, ranked by Social Capital earned.
          </p>
          <div className="mt-4 flex gap-2">
            {["week", "month", "all"].map((p) => (
              <button
                key={p} type="button"
                onClick={() => setPeriod(p)}
                className={`px-3 py-1 rounded-full text-xs font-semibold border ${
                  period === p ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-300"
                }`}
                data-testid={`blog-leaderboard-period-${p}`}
              >
                {p === "week" ? "This week" : p === "month" ? "This month" : "All time"}
              </button>
            ))}
          </div>
        </header>

        {loading ? (
          <div className="flex items-center gap-2 text-slate-500"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
        ) : !data || data.entries.length === 0 ? (
          <div className="rounded-2xl border border-slate-200 bg-white p-10 text-center text-slate-500">
            No entries for this period yet — check back after writers earn some Social Capital.
          </div>
        ) : (
          <ol className="space-y-2" data-testid="blog-leaderboard-list">
            {data.entries.map((e, i) => (
              <li key={e.writer_id}>
                <Link
                  to={`/writers/${e.writer_id}`}
                  className="flex items-center gap-3 rounded-2xl bg-white border border-slate-200 hover:border-teal-400 p-3 transition-colors"
                  data-testid={`blog-leaderboard-row-${i + 1}`}
                >
                  <div className="w-8 text-center font-bold text-slate-400" style={{ fontFamily: "'Lora', serif" }}>
                    {i === 0 ? <Trophy className="w-5 h-5 mx-auto text-amber-500" /> : `#${i + 1}`}
                  </div>
                  <Avatar url={e.avatar_url} name={e.display_name} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      <div className="text-base font-semibold text-slate-900 truncate">
                        {e.display_name || "Anonymous writer"}
                      </div>
                      {e.verified && (
                        <CheckCircle2 className="w-4 h-4 text-teal-600 flex-none"
                                      data-testid={`blog-leaderboard-verified-${i + 1}`}
                                      aria-label="Verified writer" />
                      )}
                    </div>
                    <div className="text-xs text-slate-500">
                      {e.level} · {e.article_count} publication{e.article_count === 1 ? "" : "s"}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="inline-flex items-center gap-1 text-teal-700 font-bold" data-testid={`blog-leaderboard-points-${i + 1}`}>
                      <Sparkles className="w-3.5 h-3.5" /> +{e.week_points.toLocaleString()}
                    </div>
                    <div className="text-[10px] text-slate-400 uppercase tracking-widest">
                      {period === "all" ? "total" : `this ${period}`}
                    </div>
                  </div>
                </Link>
              </li>
            ))}
          </ol>
        )}
      </div>
    </div>
  );
}
